// ─── dexStatus: DexScreener paid + boosted status, one owner ───
//
// Two DIFFERENT claims about a coin, deliberately kept separate:
//
//   PAID    — the team bought a DexScreener listing product (token profile,
//             community takeover, token ad, trending bar ad). From
//             /orders/v1/solana/<mint>, counting only status 'approved'.
//             Says: someone spent money on presentation.
//   BOOSTED — active DexScreener boosts. Already broadcast by our server on
//             the main data WS (boost_new / boosts_list), so it costs no
//             network here at all; callers pass the mint set in.
//             Says: someone is buying visibility right now.
//
// A chip is a claim about the market, so these get separate chips (DEX / 🚀)
// rather than one fuzzy "promoted" badge.
//
// RATE LIMITS ARE THE WHOLE DESIGN PROBLEM. The orders endpoint is per-token
// and the scan list can hold 50+ cards, so the naive version (fetch on every
// card render) would burn the quota in one repaint and get the app throttled.
// Hence: permanent per-mint cache, in-flight dedupe, a small concurrency cap,
// and a FIFO queue. Cards ask; the queue decides when.

const PAID_TTL_MS = 30 * 60_000     // paid status barely changes; re-check hourly-ish
const MAX_INFLIGHT = 3              // gentle on the shared endpoint
const cache = new Map()             // mint -> { paid, types, ts }
const inflight = new Map()          // mint -> Promise (dedupe concurrent askers)
const queue = []                    // [{ mint, resolve }]
const subs = new Set()              // () => void, fired when any entry settles
let active = 0

const notify = () => { for (const fn of subs) { try { fn() } catch {} } }

/** Subscribe to cache updates (components re-render on resolve). */
export function onDexStatus(fn) {
  subs.add(fn)
  return () => subs.delete(fn)
}

async function fetchPaid(mint) {
  try {
    const res = await fetch(`https://api.dexscreener.com/orders/v1/solana/${mint}`)
    if (!res.ok) throw new Error(String(res.status))
    const json = await res.json()
    // Shape: [{ type, status, paymentTimestamp }]. ONLY 'approved' counts —
    // 'processing' means someone started paying, which is not the same claim.
    const ok = Array.isArray(json) ? json.filter(o => o?.status === 'approved') : []
    return { paid: ok.length > 0, types: [...new Set(ok.map(o => o.type))], ts: Date.now() }
  } catch {
    // Network/limit failure caches SHORT so it retries, unlike a real answer.
    return { paid: false, types: [], ts: Date.now(), err: true }
  }
}

function pump() {
  while (active < MAX_INFLIGHT && queue.length) {
    const { mint } = queue.shift()
    active++
    const p = fetchPaid(mint).then(v => {
      cache.set(mint, v)
      inflight.delete(mint)
      active--
      notify()
      pump()
      return v
    })
    inflight.set(mint, p)
  }
}

/**
 * Paid status for a mint. Returns the cached value immediately (or null) and
 * schedules a fetch when missing/stale — never blocks a render.
 */
export function getDexPaid(mint) {
  if (!mint) return null
  const hit = cache.get(mint)
  const fresh = hit && !hit.err && (Date.now() - hit.ts) < PAID_TTL_MS
  const retry = hit && hit.err && (Date.now() - hit.ts) > 60_000
  if (!hit || retry || (!fresh && !hit.err)) {
    if (!inflight.has(mint) && !queue.some(q => q.mint === mint)) {
      queue.push({ mint })
      pump()
    }
  }
  return hit || null
}

/** Build a Set of boosted mints from ScanPanel's boostFeeds ({latest, top}). */
export function boostedMints(feeds) {
  const s = new Set()
  if (!feeds) return s
  for (const key of ['latest', 'top']) {
    for (const b of (feeds[key] || [])) {
      const m = b?.mint || b?.tokenAddress
      if (m) s.add(m)
    }
  }
  return s
}
